import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Divider,
  Grid,
  Header,
  Icon,
  Label,
  List,
  Loader,
  Message,
  Segment,
  Table,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";

import { getTransportTask, getTransportTasks } from "api/backendAPI";
import {
  buildTimeline,
  deriveTaskRow,
  loadAtomicDetail,
  openExceptions,
  validateTaskListResponse,
} from "utils/transportTaskViewModel";

import "./TransportTasks.scss";

const UNKNOWN = "—";
const DETAIL_CONCURRENCY = 4;
const PILOT_DETAIL_LIMIT = 12;

export function selectPilotSummaries(summaries) {
  return summaries.slice(0, PILOT_DETAIL_LIMIT);
}

async function mapWithConcurrency(items, mapper, concurrency) {
  const results = new Array(items.length);
  let nextIndex = 0;
  async function run() {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index]);
    }
  }
  const workers = new Array(Math.min(concurrency, items.length)).fill(null).map(run);
  await Promise.all(workers);
  return results;
}

function formatDate(value, language) {
  if (!value) return UNKNOWN;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return UNKNOWN;
  return new Intl.DateTimeFormat(language === "fi" ? "fi-FI" : "en-GB", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function statusColor(status) {
  switch ((status || "").toLowerCase()) {
    case "active": return "green";
    case "blocked": return "red";
    case "planned": return "blue";
    case "completed": return "grey";
    default: return "yellow";
  }
}

function valueOrMissing(value, t) {
  return value ? value : <span className="transportTasks__missing">{t("transportTasks.missing")}</span>;
}

export function assignmentDriverName(assignment) {
  return assignment && (assignment.driver_display_name || assignment.driver_id);
}

function ownerName(profile, t) {
  if (profile === "junior") return t("transportTasks.ownerProfiles.junior");
  if (profile === "senior") return t("transportTasks.ownerProfiles.senior");
  return t("transportTasks.ownerProfiles.unassigned");
}

function detailUncertainty(row, t) {
  const key = row.detailState === "notLoaded" ? "notLoaded" : row.detailState === "contract" ? "contractUnknown" : row.detailState === "stale" ? "staleUnknown" : "uncertain";
  return <Label basic color="orange" data-testid="transport-task-uncertain">{t(`transportTasks.${key}`)}</Label>;
}

function EmptyState({ t }) {
  return (
    <Message icon data-testid="transport-task-empty">
      <Icon name="check circle outline" />
      <Message.Content>
        <Message.Header>{t("transportTasks.emptyTitle")}</Message.Header>
        {t("transportTasks.emptyBody")}
      </Message.Content>
    </Message>
  );
}

export function TransportTaskListView({ rows, loading, error, t, language }) {
  if (loading) return <Loader active inline="centered" content={t("transportTasks.loading")} />;
  if (error) return <Message negative header={t("transportTasks.errorTitle")} content={t("transportTasks.errorBody")} />;
  if (!rows.length) return <EmptyState t={t} />;

  return (
    <div className="transportTasks__tableWrap">
      <Table selectable striped stackable data-testid="transport-task-list">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t("transportTasks.task")}</Table.HeaderCell>
            <Table.HeaderCell>{t("transportTasks.assignment")}</Table.HeaderCell>
            <Table.HeaderCell>{t("transportTasks.nextStop")}</Table.HeaderCell>
            <Table.HeaderCell>{t("transportTasks.latestVerified")}</Table.HeaderCell>
            <Table.HeaderCell>{t("transportTasks.exceptions")}</Table.HeaderCell>
            <Table.HeaderCell>{t("transportTasks.risk")}</Table.HeaderCell>
            <Table.HeaderCell>{t("transportTasks.version")}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map(row => {
            const assignment = row.assignment;
            const next = row.nextStop;
            const latest = row.latestVerifiedEvent;
            return (
              <Table.Row key={row.summary.transport_task_id} warning={row.uncertain} negative={!row.uncertain && row.exceptions.length > 0}>
                <Table.Cell>
                  <Link className="transportTasks__taskLink" to={{
                    pathname: `/transport-tasks/${row.summary.transport_task_id}`,
                    state: { minimumVersion: row.summary.version },
                  }}>
                    {row.summary.title}
                  </Link>
                  <div className="transportTasks__meta">
                    <Label size="tiny" color={statusColor(row.summary.status)}>{row.summary.status}</Label>
                    {row.summary.order_ref} · {row.summary.customer_ref}
                  </div>
                  <div className="transportTasks__sourceTime">
                    {t("transportTasks.updated")}: {formatDate(row.summary.updated_at, language)}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  {row.uncertain ? detailUncertainty(row, t) : (
                    <>
                      <div><strong>{t("transportTasks.owner")}:</strong> {ownerName(row.detail.owner_agent_profile, t)}</div>
                      <strong>{valueOrMissing(assignment && assignment.vehicle_ref, t)}</strong>
                      <div>{t("transportTasks.driver")}: {valueOrMissing(assignmentDriverName(assignment), t)}</div>
                      <div>{t("transportTasks.trailer")}: {valueOrMissing(assignment && (assignment.trailer_ref || assignment.equipment_ref), t)}</div>
                    </>
                  )}
                </Table.Cell>
                <Table.Cell className={!next ? "transportTasks__missingCell" : ""}>
                  {row.uncertain ? detailUncertainty(row, t) : next ? (
                    <>
                      <strong>{next.sequence}. {next.location_name}</strong>
                      <div>{next.kind} · {next.status}</div>
                      <div className={row.deadlineState === "overdue" ? "transportTasks__overdue" : ""}>
                        {t("transportTasks.deadline")}: {formatDate(row.deadline, language)}
                      </div>
                    </>
                  ) : (
                    <Label basic color="orange" data-testid="transport-task-missing-stop">{t("transportTasks.noNextStop")}</Label>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {row.uncertain ? detailUncertainty(row, t) : latest ? (
                    <>
                      <strong>{latest.stop.location_name}</strong>
                      <div>{latest.from_status} → {latest.to_status}</div>
                      <div>{t("transportTasks.sourceObservedAt")}: {formatDate(latest.observed_at, language)}</div>
                    </>
                  ) : <span className="transportTasks__missing">{t("transportTasks.noVerifiedEvent")}</span>}
                </Table.Cell>
                <Table.Cell>
                  {row.uncertain ? detailUncertainty(row, t) : row.exceptions.length ? (
                    <Label color="red" data-testid="transport-task-exception">
                      {row.exceptions.length} {t("transportTasks.open")}
                    </Label>
                  ) : <Label basic color="green">{t("transportTasks.none")}</Label>}
                </Table.Cell>
                <Table.Cell>
                  {row.uncertain ? detailUncertainty(row, t) : <>
                    {row.deadlineState === "overdue" && <Label color="red">{t("transportTasks.overdue")}</Label>}
                    {row.deadlineState === "scheduled" && <Label basic color="blue">{t("transportTasks.deadlinePending")}</Label>}
                    {row.deadlineState === "unknown" && <Label basic color="orange">{t("transportTasks.unknownRisk")}</Label>}
                  </>}
                </Table.Cell>
                <Table.Cell>
                  v{row.summary.version}
                  <div className="transportTasks__etag">ETag {row.uncertain ? t("transportTasks.unknown") : row.etag}</div>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
}

function TaskFacts({ task, etag, t, language }) {
  const assignment = task.assignment;
  const schedule = task.schedule;
  const reporting = schedule && schedule.reporting_instruction;
  return (
    <Grid stackable columns={3}>
      <Grid.Column>
        <Header as="h3">{t("transportTasks.assignment")}</Header>
        <List>
          <List.Item><strong>{t("transportTasks.owner")}:</strong> {ownerName(task.owner_agent_profile, t)}</List.Item>
          <List.Item><strong>{t("transportTasks.vehicle")}:</strong> {valueOrMissing(assignment && assignment.vehicle_ref, t)}</List.Item>
          <List.Item><strong>{t("transportTasks.driver")}:</strong> {valueOrMissing(assignmentDriverName(assignment), t)}</List.Item>
          <List.Item><strong>{t("transportTasks.trailer")}:</strong> {valueOrMissing(assignment && assignment.trailer_ref, t)}</List.Item>
          <List.Item><strong>{t("transportTasks.equipment")}:</strong> {valueOrMissing(assignment && assignment.equipment_ref, t)}</List.Item>
          <List.Item><strong>{t("transportTasks.source")}:</strong> {valueOrMissing(assignment && assignment.source, t)}</List.Item>
        </List>
      </Grid.Column>
      <Grid.Column>
        <Header as="h3">{t("transportTasks.schedule")}</Header>
        <List>
          <List.Item><strong>{t("transportTasks.plannedStart")}:</strong> {formatDate(task.planned_start, language)}</List.Item>
          <List.Item><strong>{t("transportTasks.plannedEnd")}:</strong> {formatDate(task.planned_end, language)}</List.Item>
          <List.Item><strong>{t("transportTasks.nextCheck")}:</strong> {formatDate(schedule && schedule.next_check_at, language)}</List.Item>
          <List.Item><strong>{t("transportTasks.schedulerOwner")}:</strong> {valueOrMissing(schedule && schedule.scheduler_owner, t)}</List.Item>
          <List.Item><strong>{t("transportTasks.createdAt")}:</strong> {formatDate(task.created_at, language)}</List.Item>
          <List.Item><strong>{t("transportTasks.updatedAt")}:</strong> {formatDate(task.updated_at, language)}</List.Item>
        </List>
      </Grid.Column>
      <Grid.Column>
        <Header as="h3">{t("transportTasks.reporting")}</Header>
        {reporting ? (
          <List>
            <List.Item>{reporting.instruction}</List.Item>
            <List.Item><strong>{t("transportTasks.channel")}:</strong> {valueOrMissing(reporting.channel, t)}</List.Item>
            <List.Item><strong>{t("transportTasks.cadence")}:</strong> {valueOrMissing(reporting.cadence, t)}</List.Item>
            <List.Item><Label basic color={reporting.active ? "green" : "grey"}>{reporting.active ? t("transportTasks.active") : t("transportTasks.inactive")}</Label></List.Item>
          </List>
        ) : <Message warning size="small">{t("transportTasks.noReporting")}</Message>}
        <div className="transportTasks__version">v{task.version} · ETag {valueOrMissing(etag, t)}</div>
      </Grid.Column>
    </Grid>
  );
}

function Stops({ stops, t, language }) {
  if (!stops || !stops.length) return <Message warning>{t("transportTasks.noStops")}</Message>;
  return (
    <Card.Group itemsPerRow={3} stackable>
      {stops.slice().sort((a, b) => a.sequence - b.sequence).map(stop => (
        <Card key={stop.stop_id}>
          <Card.Content>
            <Card.Header>{stop.sequence}. {stop.location_name}</Card.Header>
            <Card.Meta>{stop.kind} · {stop.status}</Card.Meta>
            <Card.Description>
              <div>{valueOrMissing(stop.address, t)}</div>
              <div>{formatDate(stop.window_start, language)} – {formatDate(stop.window_end, language)}</div>
              {stop.instructions && <p>{stop.instructions}</p>}
            </Card.Description>
          </Card.Content>
        </Card>
      ))}
    </Card.Group>
  );
}

function Collections({ task, t, language }) {
  const groups = [
    ["instructions", task.instructions, item => item.content, item => item.effective_at, item => `${t("transportTasks.version")} ${item.version} · ${item.status}`],
    ["evidence", task.evidence, item => item.summary, item => item.observed_at, item => `${item.evidence_type} · ${item.source_system} · ${t("transportTasks.sourceObservedAt")}:`],
    ["messages", task.messages, item => item.body_summary, item => item.occurred_at, item => `${item.direction} · ${item.channel} · ${item.initial_delivery_status || item.delivery_status}`],
    ["exceptions", task.exceptions, item => item.description, item => item.detected_at, item => `${item.severity} · ${item.code} · ${item.status}`],
  ];
  return (
    <Grid stackable columns={2}>
      {groups.map(([name, items, title, time, meta]) => (
        <Grid.Column key={name}>
          <Segment>
            <Header as="h3">{t(`transportTasks.${name}`)}</Header>
            {!items || !items.length ? <p className="transportTasks__missing">{t("transportTasks.noRecords")}</p> : (
              <List divided relaxed>
                {items.map((item, index) => (
                  <List.Item key={item[`${name.slice(0, -1)}_id`] || index}>
                    <List.Content>
                      <List.Header>{title(item)}</List.Header>
                      <List.Description>{meta(item)} · {formatDate(time(item), language)}</List.Description>
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            )}
          </Segment>
        </Grid.Column>
      ))}
    </Grid>
  );
}

export function TransportTaskDetailView({ task, etag, loading, error, lastLoaded, onRefresh, t, language }) {
  if (loading) return (
    <Message warning data-testid="transport-task-detail-unknown">
      <Loader active inline="centered" content={t("transportTasks.loadingUnknown")} />
    </Message>
  );
  if (error || !task) return (
    <Message negative data-testid="transport-task-detail-unknown">
      <Message.Header>{t("transportTasks.detailUnknown")}</Message.Header>
      <p>{t(`transportTasks.detailErrors.${error || "network"}`)}</p>
      <Button basic onClick={onRefresh} icon="refresh" content={t("transportTasks.refresh")} />
    </Message>
  );
  const timeline = buildTimeline(task);
  const exceptions = openExceptions(task);
  return (
    <div data-testid="transport-task-detail">
      <Link to="/transport-tasks"><Icon name="arrow left" />{t("transportTasks.backToList")}</Link>
      <Button floated="right" basic onClick={onRefresh} icon="refresh" content={t("transportTasks.refresh")} />
      <Header as="h1">
        {task.title}
        <Header.Subheader>{task.order_ref} · {task.customer_name_snapshot || task.customer_ref}</Header.Subheader>
      </Header>
      <div className="transportTasks__detailLabels">
        <Label color={statusColor(task.status)}>{task.status}</Label>
        <Label basic>{task.priority}</Label>
        <Label basic>{task.source_system}: {task.source_ref}</Label>
        {exceptions.length > 0 && <Label color="red">{exceptions.length} {t("transportTasks.openExceptions")}</Label>}
      </div>
      <div className="transportTasks__loadedAt">{t("transportTasks.lastLoaded")}: {formatDate(lastLoaded, language)}</div>
      {task.description && <p className="transportTasks__description">{task.description}</p>}
      <Segment><TaskFacts task={task} etag={etag} t={t} language={language} /></Segment>

      <Header as="h2">{t("transportTasks.stops")}</Header>
      <Stops stops={task.stops} t={t} language={language} />

      <Header as="h2">{t("transportTasks.timeline")}</Header>
      {!timeline.length ? <Message warning>{t("transportTasks.noTimeline")}</Message> : (
        <List divided relaxed className="transportTasks__timeline">
          {timeline.map(item => (
            <List.Item key={item.id}>
              <Label horizontal basic color={item.uncertain ? "orange" : undefined}>{t(`transportTasks.timelineKinds.${item.kind}`)}</Label>
              <List.Content>
                <List.Header>{item.title}</List.Header>
                <List.Description>
                  {formatDate(item.time, language)}
                  {item.source ? ` · ${item.source}` : ""}
                  {item.status ? ` · ${item.status}` : ""}
                  {item.evidenceRef ? ` · ${t("transportTasks.evidenceRef")}: ${item.evidenceRef}` : ""}
                  {item.outcome ? ` · ${t("transportTasks.outcome")}: ${item.outcome}` : ""}
                  {item.reason ? ` · ${t("transportTasks.reason")}: ${item.reason}` : ""}
                  {item.recordedBy ? ` · ${t("transportTasks.recordedBy")}: ${item.recordedBy}` : ""}
                </List.Description>
              </List.Content>
            </List.Item>
          ))}
        </List>
      )}
      <Divider />
      <Collections task={task} t={t} language={language} />
    </div>
  );
}

export function TransportTaskListPage() {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ rows: [], loading: true, error: false, lastLoaded: null });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    async function load() {
      setState(current => ({ ...current, rows: [], loading: true, error: false }));
      try {
        const result = await getTransportTasks();
        if (!validateTaskListResponse(result.data, result.contractVersion)) throw Object.assign(new Error("invalid list contract"), { kind: "contract" });
        const summaries = result.data.items;
        const enrichment = selectPilotSummaries(summaries);
        const initialRows = summaries.map((summary, index) => deriveTaskRow(summary, null, null, new Date(), index < PILOT_DETAIL_LIMIT ? "loading" : "notLoaded"));
        if (active) setState({ rows: initialRows, loading: false, error: false, lastLoaded: new Date().toISOString() });
        const enrichedRows = await mapWithConcurrency(enrichment, async summary => {
          try {
            const response = await loadAtomicDetail(() => getTransportTask(summary.transport_task_id), summary.transport_task_id, summary.version);
            return deriveTaskRow(summary, response.data, response.etag, new Date(), null, response.contractVersion);
          } catch (error) {
            return deriveTaskRow(summary, null, null, new Date(), error.kind || "network");
          }
        }, DETAIL_CONCURRENCY);
        const rows = enrichedRows.concat(initialRows.slice(PILOT_DETAIL_LIMIT));
        if (active) setState({ rows, loading: false, error: false, lastLoaded: new Date().toISOString() });
      } catch (error) {
        if (active) setState(current => ({ ...current, rows: [], loading: false, error: error.kind || "network" }));
      }
    }
    load();
    return () => { active = false; };
  }, [reloadKey]);

  return (
    <main className="transportTasks">
      <Header as="h1">{t("transportTasks.title")}</Header>
      <Button floated="right" basic onClick={() => setReloadKey(key => key + 1)} icon="refresh" content={t("transportTasks.refresh")} />
      <p className="transportTasks__lead">{t("transportTasks.lead")}</p>
      {state.lastLoaded && <p className="transportTasks__loadedAt">{t("transportTasks.lastLoaded")}: {formatDate(state.lastLoaded, i18n.language)}</p>}
      <TransportTaskListView {...state} t={t} language={i18n.language} />
    </main>
  );
}

export function TransportTaskDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const minimumVersion = location.state && Number.isInteger(location.state.minimumVersion) ? location.state.minimumVersion : null;
  const [state, setState] = useState({ task: null, etag: null, loading: true, error: null, lastLoaded: null });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setState(current => ({ ...current, task: null, etag: null, loading: true, error: null }));
    loadAtomicDetail(() => getTransportTask(id), id, minimumVersion)
      .then(response => active && setState({ task: response.data, etag: response.etag, loading: false, error: null, lastLoaded: new Date().toISOString() }))
      .catch(error => active && setState(current => ({ ...current, task: null, etag: null, loading: false, error: error.kind || "network" })));
    return () => { active = false; };
  }, [id, minimumVersion, reloadKey]);

  const props = useMemo(() => ({ ...state, t, language: i18n.language, onRefresh: () => setReloadKey(key => key + 1) }), [state, t, i18n.language]);
  return <main className="transportTasks"><TransportTaskDetailView {...props} /></main>;
}

export default TransportTaskListPage;
